import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import cloneDeep from 'lodash/cloneDeep';
import { useNavigate } from 'react-router-dom';
import './file-upload.scss';
import Button from '@components/buttons/Button';
import { projectsApi } from '@api/projectsApi';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { TFunction, useTranslation } from 'react-i18next';
import { selectCurrentProjectId } from '@redux/selectors/selectors';
import { isErrorResponse } from '@utils/utils';
import { useProjectContext } from '@context/ProjectContext';
import { useToastContext } from '@context/toastContext';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import { useTheme } from '@context/themeContext';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import { CloseIcon } from '@icons/icons/CloseIcon';
import { ImagePlaceholderIcon } from '@icons/icons/ImagePlaceholderIcon';

export const getFields = (t: TFunction) => [
    {
        labelName: t('file-upload.config-file'),
        name: 'config',
        type: 'select',
        required: true,
        namePattern: '.*(config).*',
    },
    {
        labelName: t('file-upload.network'),
        name: 'network',
        type: 'select',
        required: true,
        namePattern: '.*(network).*',
    },
    {
        labelName: t('file-upload.plans'),
        name: 'plans',
        type: 'select',
        required: true,
        namePattern: '.*(plans).*',
    },
    {
        labelName: t('file-upload.transit-schedule-file'),
        name: 'transitSchedule',
        type: 'select',
        namePattern: '.*(schedule).*',
    },
    {
        labelName: t('file-upload.transit-vehicles-file'),
        name: 'transitVehicles',
        type: 'select',
        namePattern: '.*(vehicles).*',
    },
    {
        labelName: t('file-upload.attributes-file'),
        name: 'attributesFile',
        type: 'select',
        namePattern: '.*(attributes).*',
    },
    { labelName: t('file-upload.vehicles-file'), name: 'vehicles', type: 'select' },
    {
        labelName: t('file-upload.epsg-code'),
        name: 'epsgCode',
        type: 'input',
        placeholder: 'for example: 25833',
        required: true,
    },
    {
        labelName: t('file-upload.percentage-of-agents-in-model'),
        name: 'percentageOfAgentsInModel',
        type: 'input',
        placeholder: 'for example: 10',
        required: true,
        pattern: '[1-9]{1,2}[0]?|100',
    },
];

const FilesUploader = ({ setVisible }) => {
    const [disabledSubmit, setDisabledSubmit] = useState(true);
    const projectId = useTypedSelector(selectCurrentProjectId);
    const [filesById, setFilesById] = useState<{ [key: string]: File }>({});
    const [saving, setSaving] = useState<boolean>(false);
    const formRef = useRef<HTMLFormElement>(null);
    const { loadProjects } = useProjectContext();
    const navigate = useNavigate();
    const { addToast } = useToastContext();
    const { t } = useTranslation();
    const { theme } = useTheme();

    const dropHandler = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
            const files = cloneDeep(acceptedFiles);
            const acceptedFilesById = files.reduce((acc, file) => {
                if (!acc[file.name]) {
                    acc[file.name] = file;
                }
                return acc;
            }, {});

            setFilesById(prevState => ({ ...prevState, ...acceptedFilesById }));
        },
        []
    );

    const { getRootProps, getInputProps, open } = useDropzone({
        noClick: true,
        noKeyboard: true,
        accept: {
            'application/xml': ['.xml'],
            'application/gzip': ['.gz', '.zip'],
        },
        onDrop: dropHandler,
    });

    const getFormData = useCallback(
        (formElem: HTMLFormElement) => {
            const formData = new FormData(formElem);
            for (const [name, val] of Array.from(formData.entries())) {
                if (filesById[val as string]) {
                    const file = filesById[val as string];
                    formData.set(name, file, file.name);
                } else {
                    if (val) {
                        formData.set(name, val);
                    } else {
                        formData.delete(name);
                    }
                }
            }
            return formData;
        },
        [filesById]
    );

    const checkFormSubmitAbilityOnChange = useCallback(() => {
        const formData = getFormData(formRef.current!);
        const requiredFieldsFilled = getFields(t).every(({ name, required }) =>
            required ? required && formData.has(name) : true
        );
        setDisabledSubmit(!requiredFieldsFilled || !formRef.current?.checkValidity());
    }, [t, getFields, filesById, disabledSubmit]);

    useEffect(() => {
        checkFormSubmitAbilityOnChange();
    }, [filesById, checkFormSubmitAbilityOnChange]);

    const filesList = useMemo(() => {
        return Object.values(filesById);
    }, [filesById]);

    const deleteFile = useCallback(
        (name: string) => {
            setFilesById(prevState => {
                const { [name]: removedFile, ...restFiles } = prevState;
                return restFiles;
            });
        },
        [setFilesById, setFilesById]
    );

    const createProject = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!projectId) {
                console.error('Replan. FilesUploader submit error: projectId disappears');
                return;
            }

            const formData = getFormData(event.currentTarget);

            setSaving(true);
            const resp = await projectsApi.uploadFiles(projectId, formData);
            setSaving(false);

            if (!isErrorResponse(resp)) {
                await loadProjects();
                navigate(`./${projectId}`);
            } else {
                console.error('FilesUploader createProject error:', resp);
                const { error, status } = resp;
                addToast(
                    `${error['code']}. ${error['status']}: ${error['message']}`,
                    'error',
                    false
                );
            }
        },
        [filesById]
    );

    const closeWindow = useCallback(() => {
        setVisible('');
    }, [setVisible]);

    return (
        <LoadingBackdrop isVisible={saving} transparent>
            <div className="dnd-bg">
                <div className="dnd-container">
                    <div className="content-header">
                        <span>{t('file-upload.project-creation-title')}</span>
                        <IconButton icon={CloseIcon} onClick={closeWindow} />
                    </div>
                    <div className="content">
                        <div className="top-side">
                            <div className="image">
                                <ImagePlaceholderIcon width={127} height={127} />
                            </div>
                            <div {...getRootProps({ className: 'dropzone' })}>
                                <input data-testid="dropzone" {...getInputProps()} />
                                <div onClick={open}>
                                    <Button text={t('file-upload.upload')} colorType="light" />
                                </div>
                            </div>
                        </div>
                        <div className="bottom-side">
                            <div className="left-side">
                                <p className="files-list-header">{t('file-upload.files')}</p>
                                <ul className="files-list">
                                    {filesList.map((file: File, index) => (
                                        <div className="list-item-block" key={index}>
                                            <li>
                                                {file.name} - {file.size} bytes
                                            </li>
                                            <IconButton
                                                icon={CloseIcon}
                                                onClick={() => deleteFile(file.name)}
                                                data-testid={`${file.name}_deleteBtn`}
                                            />
                                        </div>
                                    ))}
                                </ul>
                            </div>
                            <div className="right-side">
                                <form
                                    name="filesUploaderForm"
                                    className="form-edit"
                                    onChange={checkFormSubmitAbilityOnChange}
                                    onSubmit={createProject}
                                    ref={formRef}
                                >
                                    {getFields(t).map(
                                        (
                                            {
                                                name,
                                                labelName,
                                                type,
                                                required,
                                                placeholder,
                                                pattern,
                                                namePattern,
                                            },
                                            index
                                        ) => {
                                            const regex = new RegExp(namePattern!, 'i');

                                            return (
                                                <div key={`block_${index}`} className="input-block">
                                                    <p>
                                                        {labelName} {required ? '*' : ''}
                                                    </p>
                                                    {type === 'select' ? (
                                                        <select
                                                            name={name}
                                                            required={required}
                                                            className={`${theme}`}
                                                        >
                                                            <option value={''} selected={true}>
                                                                {t('file-upload.select-file')}
                                                            </option>
                                                            {filesList.map(
                                                                (file: File, fileInd: number) => {
                                                                    return (
                                                                        <option
                                                                            key={`${index}_${fileInd}`}
                                                                            value={file.name}
                                                                            {...(namePattern &&
                                                                            regex.test(file.name)
                                                                                ? { selected: true }
                                                                                : {})}
                                                                        >
                                                                            {file.name}
                                                                        </option>
                                                                    );
                                                                }
                                                            )}
                                                        </select>
                                                    ) : (
                                                        <input
                                                            className={`${theme}`}
                                                            type="text"
                                                            name={name}
                                                            defaultValue=""
                                                            placeholder={placeholder}
                                                            required
                                                            pattern={pattern}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }
                                    )}
                                    <div className="button-block">
                                        <div className="transparent-button-block">
                                            <Button
                                                onClick={() => setVisible('')}
                                                text={t('common.cancel')}
                                                colorType="light"
                                            />
                                        </div>
                                        <Button
                                            // className="submitBtn"
                                            type="submit"
                                            disabled={disabledSubmit}
                                            text={t('common.create')}
                                            colorType="dark"
                                        />
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </LoadingBackdrop>
    );
};

export default FilesUploader;
